// client/src/pages/admin/ManageMusicAttribution.js
import { useState, useEffect } from 'react';
import api from '../../services/api.js';
import Button from '../../components/ui/Button.js';

const LICENSE_TYPES = [
  { value: 'cc0', label: 'CC0 - Public Domain' },
  { value: 'cc-by', label: 'CC BY - Attribution' },
  { value: 'cc-by-sa', label: 'CC BY-SA - Attribution-ShareAlike' },
  { value: 'cc-by-nd', label: 'CC BY-ND - Attribution-NoDerivs' },
  { value: 'cc-by-nc', label: 'CC BY-NC - Attribution-NonCommercial' },
  { value: 'cc-by-nc-sa', label: 'CC BY-NC-SA - Attribution-NonCommercial-ShareAlike' },
  { value: 'cc-by-nc-nd', label: 'CC BY-NC-ND - Attribution-NonCommercial-NoDerivs' },
  { value: 'fesliyan-commercial', label: 'Fesliyan Studios Commercial' },
  { value: 'royalty-free', label: 'Royalty Free' },
  { value: 'custom', label: 'Custom / Other' },
];

function getLicenseTypeLabel(value) {
  const found = LICENSE_TYPES.find((t) => t.value === value);
  return found ? found.label : value;
}

// ─── Attribution Modal ────────────────────────────────────
function AttributionModal({ attribution, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    name: attribution?.name || '',
    licensetype: attribution?.licensetype || 'cc-by',
    licenseurl: attribution?.licenseurl || '',
    description: attribution?.description || '',
  });
  const [error, setError] = useState('');

  const isEditing = !!attribution?.attributionid;

  const handleSave = () => {
    setError('');
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.licensetype) {
      setError('License type is required');
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-xl">
            {isEditing ? 'Edit Attribution' : 'Add Attribution'}
          </h2>
          <button onClick={onClose} className="text-royal-200 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
          {error && <p className="text-red-400 text-base">{error}</p>}

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Fesliyan Studios Commercial License"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">License Type *</label>
            <select
              value={form.licensetype}
              onChange={(e) => setForm({ ...form, licensetype: e.target.value })}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
            >
              {LICENSE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">License URL</label>
            <input
              type="url"
              value={form.licenseurl}
              onChange={(e) => setForm({ ...form, licenseurl: e.target.value })}
              placeholder="https://creativecommons.org/licenses/..."
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Description / License Details</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Full license text or description..."
              rows={6}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors resize-y"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 pt-4 border-t border-royal-700/50">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-200 text-base hover:text-white transition-colors">
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Attribution'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── View Details Modal ───────────────────────────────────
function ViewDetailsModal({ attribution, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-xl">License Details</h2>
          <button onClick={onClose} className="text-royal-200 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
          <div>
            <p className="text-royal-200 text-sm mb-1">Name</p>
            <p className="text-white text-base font-medium">{attribution.name}</p>
          </div>

          <div>
            <p className="text-royal-200 text-sm mb-1">License Type</p>
            <p className="text-white text-base">{getLicenseTypeLabel(attribution.licensetype)}</p>
          </div>

          {attribution.licenseurl && (
            <div>
              <p className="text-royal-200 text-sm mb-1">License URL</p>
              <a
                href={attribution.licenseurl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold hover:text-gold-light text-base underline break-all"
              >
                {attribution.licenseurl}
              </a>
            </div>
          )}

          {attribution.description && (
            <div>
              <p className="text-royal-200 text-sm mb-1">Description</p>
              <div className="bg-royal-800/50 border border-royal-700/50 rounded-lg p-4">
                <p className="text-white text-base whitespace-pre-wrap">{attribution.description}</p>
              </div>
            </div>
          )}

          <div className="text-royal-400 text-sm pt-2 border-t border-royal-700/50">
            <p>Created: {new Date(attribution.createdat).toLocaleDateString()}</p>
            <p>Updated: {new Date(attribution.updatedat).toLocaleDateString()}</p>
          </div>
        </div>

        <div className="flex justify-end p-6 pt-4 border-t border-royal-700/50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-royal-200 border border-royal-600 rounded-lg text-base hover:text-white hover:border-royal-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────
export default function ManageMusicAttribution() {
  const [attributions, setAttributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAttribution, setEditingAttribution] = useState(null);
  const [viewingAttribution, setViewingAttribution] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchAttributions = async () => {
    setLoading(true);
    try {
      const data = await api.get('/admin/music-attribution');
      setAttributions(data.attributions || []);
    } catch (err) {
      console.error('Fetch attributions error:', err);
      setError('Failed to load attributions');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAttributions();
  }, []);

  const handleSave = async (form) => {
    setSaving(true);
    setError('');
    try {
      if (editingAttribution?.attributionid) {
        await api.put(`/admin/music-attribution/${editingAttribution.attributionid}`, form);
      } else {
        await api.post('/admin/music-attribution', form);
      }
      setEditingAttribution(null);
      fetchAttributions();
    } catch (err) {
      setError(err.data?.error || 'Failed to save attribution');
    }
    setSaving(false);
  };

  const handleDelete = async (attribution) => {
    if (!window.confirm(`Delete "${attribution.name}"? This cannot be undone.`)) return;

    setDeleting(attribution.attributionid);
    setError('');
    try {
      await api.del(`/admin/music-attribution/${attribution.attributionid}`);
      fetchAttributions();
    } catch (err) {
      setError(err.data?.error || 'Failed to delete attribution');
    }
    setDeleting(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl text-gold">Music Attribution</h2>
          <p className="text-royal-200 text-base">{attributions.length} license type{attributions.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setEditingAttribution({})}>+ Add License Type</Button>
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-base">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      ) : attributions.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-royal-200 text-base mb-2">No attribution records yet</p>
          <button
            onClick={() => setEditingAttribution({})}
            className="text-gold text-base hover:text-gold-light transition-colors"
          >
            Add your first license type →
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {attributions.map((attr) => (
            <div
              key={attr.attributionid}
              className="bg-royal-800/40 border border-royal-700/50 rounded-xl p-4 flex items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-medium text-base truncate">{attr.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-royal-700 text-royal-200">
                    {attr.licensetype}
                  </span>
                </div>
                {attr.licenseurl && (
                  <a
                    href={attr.licenseurl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold/70 hover:text-gold text-sm truncate block"
                  >
                    {attr.licenseurl}
                  </a>
                )}
                {attr.description && (
                  <p className="text-royal-200 text-sm mt-1 line-clamp-2">{attr.description}</p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setViewingAttribution(attr)}
                  className="p-2 text-royal-200 hover:text-white rounded-lg hover:bg-royal-700/50 transition-colors"
                  title="View details"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => setEditingAttribution(attr)}
                  className="p-2 text-royal-200 hover:text-gold rounded-lg hover:bg-royal-700/50 transition-colors"
                  title="Edit"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(attr)}
                  disabled={deleting === attr.attributionid}
                  className="p-2 text-royal-200 hover:text-red-400 rounded-lg hover:bg-royal-700/50 transition-colors disabled:opacity-50"
                  title="Delete"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Add Modal */}
      {editingAttribution !== null && (
        <AttributionModal
          attribution={editingAttribution}
          onSave={handleSave}
          onClose={() => setEditingAttribution(null)}
          saving={saving}
        />
      )}

      {/* View Details Modal */}
      {viewingAttribution && (
        <ViewDetailsModal
          attribution={viewingAttribution}
          onClose={() => setViewingAttribution(null)}
        />
      )}
    </div>
  );
}